/**
 * The tone curve editor.
 *
 * Two things make this more than a spline widget. The curve is drawn by
 * sampling the *same* LUT the shader uploads, so the line on screen is exactly
 * the transfer function applied to the pixels, with no separate preview maths
 * that can drift. And the frame's own histogram sits behind it, so the toe and
 * the shoulder are placed against where the picture's tones actually are rather
 * than against an empty grid.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  buildCurveLut,
  describeCurve,
  isNeutralCurve,
  type CurveChannel,
  type CurvePoint,
} from '@lumos/core';
import { useSelectedPhoto, useStore } from '../state/store.ts';
import { Panel } from './controls/Panel.tsx';
import { Tip } from './controls/Tooltip.tsx';

/** Mirrors the `.curve-channel` colours in styles.css; canvas cannot use classes. */
const CHANNEL_INK: Record<CurveChannel, string> = {
  rgb: '#ffedd7',
  red: '#e2564a',
  green: '#62b06a',
  blue: '#5a86e0',
};

const CHANNELS: { id: CurveChannel; label: string }[] = [
  { id: 'rgb', label: 'RGB' },
  { id: 'red', label: 'Red' },
  { id: 'green', label: 'Green' },
  { id: 'blue', label: 'Blue' },
];

const PRESETS: { name: string; points: CurvePoint[] }[] = [
  { name: 'Linear', points: [{ x: 0, y: 0 }, { x: 1, y: 1 }] },
  {
    name: 'Medium contrast',
    points: [
      { x: 0, y: 0 },
      { x: 0.25, y: 0.21 },
      { x: 0.75, y: 0.79 },
      { x: 1, y: 1 },
    ],
  },
  {
    name: 'Strong contrast',
    points: [
      { x: 0, y: 0 },
      { x: 0.25, y: 0.15 },
      { x: 0.5, y: 0.5 },
      { x: 0.75, y: 0.85 },
      { x: 1, y: 1 },
    ],
  },
  {
    name: 'Matte',
    points: [
      { x: 0, y: 0.085 },
      { x: 0.3, y: 0.315 },
      { x: 0.7, y: 0.7 },
      { x: 1, y: 0.94 },
    ],
  },
  {
    // Lifted black and a rolled shoulder, but a mid-tone slope above 1: the
    // print look, not simply a flatter picture.
    name: 'Film',
    points: [
      { x: 0, y: 0.045 },
      { x: 0.16, y: 0.175 },
      { x: 0.45, y: 0.435 },
      { x: 0.72, y: 0.75 },
      { x: 1, y: 0.885 },
    ],
  },
];

/** Padding inside the canvas so points on the edges are still grabbable. */
const PAD = 10;
/** Points may not be dragged closer than this in x; the spline needs the room. */
const MIN_GAP = 0.012;
const HIT_RADIUS = 11;

const CURVE_TIP = 'Click to add a point, drag to move, alt-click to remove. Arrow keys nudge the selected one.';

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

function toByte(value: number): number {
  return Math.round(clamp(value, 0, 1) * 255);
}

function toPercent(value: number): string {
  return `${Math.round(clamp(value, 0, 1) * 100)}%`;
}

function samePoints(a: CurvePoint[], b: CurvePoint[]): boolean {
  return (
    a.length === b.length &&
    a.every((point, index) => Math.abs(point.x - b[index].x) < 1e-4 && Math.abs(point.y - b[index].y) < 1e-4)
  );
}

/**
 * Tracks an element's CSS-pixel box. Takes the element itself, not a ref, so
 * it re-attaches when the canvas is unmounted and mounted again. Measured off
 * the bounding rect rather than clientWidth: the rail is dragged to fractional
 * widths, and rounding the box before scaling it by the device pixel ratio is
 * what makes the curve resample and go soft.
 */
function useElementSize<T extends HTMLElement>(element: T | null) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    if (!element) return;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      setSize((previous) =>
        previous.width === rect.width && previous.height === rect.height
          ? previous
          : { width: rect.width, height: rect.height },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return size;
}

export function CurveEditor() {
  const photo = useSelectedPhoto();
  const stats = useStore((state) => state.stats);
  const setCurve = useStore((state) => state.setCurve);

  const containerRef = useRef<HTMLDivElement>(null);
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const size = useElementSize(canvas);
  const dragRef = useRef<{ index: number; lockedX: number } | null>(null);
  /** Last points written this gesture, so the commit cannot land a stale array. */
  const pendingRef = useRef<CurvePoint[] | null>(null);
  const hintId = useId();

  const [channel, setChannel] = useState<CurveChannel>('rgb');
  const [selected, setSelected] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [cursor, setCursor] = useState<number | null>(null);

  const points = photo?.recipe.curves[channel];
  const lut = useMemo(() => buildCurveLut(points ?? [], 256), [points]);

  const histogram = useMemo(() => {
    if (!stats) return null;
    return channel === 'rgb' ? stats.luminance : stats.histograms[channel];
  }, [stats, channel]);

  const draw = useCallback(() => {
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || size.width === 0 || size.height === 0 || !points) return;

    const dpr = window.devicePixelRatio || 1;
    const deviceWidth = Math.max(1, Math.round(size.width * dpr));
    const deviceHeight = Math.max(1, Math.round(size.height * dpr));
    if (canvas.width !== deviceWidth) canvas.width = deviceWidth;
    if (canvas.height !== deviceHeight) canvas.height = deviceHeight;

    const w = size.width;
    const h = size.height;
    const ink = CHANNEL_INK[channel];
    const toX = (x: number) => PAD + x * (w - PAD * 2);
    const toY = (y: number) => h - PAD - y * (h - PAD * 2);

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    if (histogram && histogram.length > 0) {
      // The first and last bins hold every clipped pixel, so a blown sky or a
      // pure-black background would flatten everything else to nothing. Scale
      // to the tallest interior bin instead and let the ends run off the top.
      let peak = 0;
      for (let i = 1; i < histogram.length - 1; i += 1) peak = Math.max(peak, histogram[i]);
      if (peak <= 0) for (const value of histogram) peak = Math.max(peak, value);

      if (peak > 0) {
        ctx.beginPath();
        ctx.moveTo(toX(0), toY(0));
        histogram.forEach((value, index) => {
          const x = toX((index + 0.5) / histogram.length);
          const y = toY(Math.min(1, value / peak));
          ctx.lineTo(x, y);
        });
        ctx.lineTo(toX(1), toY(0));
        ctx.closePath();
        ctx.globalAlpha = 0.12;
        ctx.fillStyle = ink;
        ctx.fill();
        ctx.globalAlpha = 0.28;
        ctx.lineWidth = 1;
        ctx.strokeStyle = ink;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }

    ctx.lineWidth = 1;
    ctx.strokeStyle = '#2a231d';
    for (let i = 1; i < 4; i += 1) {
      const x = Math.round(toX(i / 4)) + 0.5;
      const y = Math.round(toY(i / 4)) + 0.5;
      ctx.beginPath();
      ctx.moveTo(x, PAD);
      ctx.lineTo(x, h - PAD);
      ctx.moveTo(PAD, y);
      ctx.lineTo(w - PAD, y);
      ctx.stroke();
    }

    ctx.strokeStyle = '#40372e';
    ctx.strokeRect(Math.round(PAD) + 0.5, Math.round(PAD) + 0.5, w - PAD * 2 - 1, h - PAD * 2 - 1);

    ctx.save();
    ctx.setLineDash([2, 3]);
    ctx.strokeStyle = '#6c5f51';
    ctx.globalAlpha = 0.6;
    ctx.beginPath();
    ctx.moveTo(toX(0), toY(0));
    ctx.lineTo(toX(1), toY(1));
    ctx.stroke();
    ctx.restore();

    const active = dragRef.current?.index ?? selected ?? hover;
    if (active !== null && points[active]) {
      ctx.save();
      ctx.setLineDash([1, 3]);
      ctx.strokeStyle = '#dc5000';
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.moveTo(toX(points[active].x), h - PAD);
      ctx.lineTo(toX(points[active].x), toY(points[active].y));
      ctx.lineTo(PAD, toY(points[active].y));
      ctx.stroke();
      ctx.restore();
    }

    ctx.beginPath();
    for (let i = 0; i < lut.length; i += 1) {
      const x = toX(i / (lut.length - 1));
      const y = toY(lut[i]);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = ink;
    ctx.lineJoin = 'round';
    ctx.stroke();

    points.forEach((point, index) => {
      const x = toX(point.x);
      const y = toY(point.y);
      const isActive = index === selected || index === dragRef.current?.index;
      if (index === hover || isActive) {
        ctx.beginPath();
        ctx.arc(x, y, 7, 0, Math.PI * 2);
        ctx.globalAlpha = 0.35;
        ctx.fillStyle = isActive ? '#dc5000' : ink;
        ctx.fill();
        ctx.globalAlpha = 1;
      }
      ctx.beginPath();
      ctx.arc(x, y, isActive ? 4.2 : 3.2, 0, Math.PI * 2);
      ctx.fillStyle = isActive ? '#dc5000' : '#0a0503';
      ctx.fill();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = isActive ? '#ffedd7' : ink;
      ctx.stroke();
    });
  }, [canvas, channel, histogram, hover, lut, points, selected, size]);

  useEffect(() => {
    draw();
  }, [draw]);

  if (!photo || !points) {
    return <div className="curve-hint">Open a photo to edit its tone curve.</div>;
  }

  const localPosition = (event: { clientX: number; clientY: number }) => {
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const plotWidth = rect.width - PAD * 2;
    const plotHeight = rect.height - PAD * 2;
    if (plotWidth <= 0 || plotHeight <= 0) return null;
    return {
      x: (event.clientX - rect.left - PAD) / plotWidth,
      y: (rect.bottom - PAD - event.clientY) / plotHeight,
      px: event.clientX - rect.left,
      py: event.clientY - rect.top,
      rect,
    };
  };

  const hitTest = (px: number, py: number, rect: DOMRect): number | null => {
    const toX = (x: number) => PAD + x * (rect.width - PAD * 2);
    const toY = (y: number) => rect.height - PAD - y * (rect.height - PAD * 2);
    let best: number | null = null;
    let bestDistance = HIT_RADIUS;
    points.forEach((point, index) => {
      const distance = Math.hypot(toX(point.x) - px, toY(point.y) - py);
      if (distance <= bestDistance) {
        best = index;
        bestDistance = distance;
      }
    });
    return best;
  };

  const removePoint = (index: number) => {
    if (index <= 0 || index >= points.length - 1) return;
    const next = points.filter((_, i) => i !== index);
    setCurve(channel, next, true);
    setSelected(null);
    setHover(null);
    dragRef.current = null;
    pendingRef.current = null;
  };

  const movePoint = (index: number, x: number, y: number, commit: boolean) => {
    const lower = index === 0 ? 0 : points[index - 1].x + MIN_GAP;
    const upper = index === points.length - 1 ? 1 : points[index + 1].x - MIN_GAP;
    const next = points.map((point, i) =>
      i === index ? { x: clamp(x, lower, upper), y: clamp(y, 0, 1) } : point,
    );
    pendingRef.current = next;
    setCurve(channel, next, commit);
  };

  /** One history entry per gesture: everything before this wrote without committing. */
  const commitGesture = () => {
    setCurve(channel, pendingRef.current ?? points, true);
    pendingRef.current = null;
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const local = localPosition(event);
    if (!local) return;
    event.preventDefault();
    containerRef.current?.focus();
    const index = hitTest(local.px, local.py, local.rect);

    if (event.altKey) {
      if (index !== null) removePoint(index);
      return;
    }

    if (index !== null) {
      dragRef.current = { index, lockedX: points[index].x };
      setSelected(index);
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }

    const x = clamp(local.x, 0, 1);
    const y = clamp(local.y, 0, 1);
    // Refuse to stack a new point on top of a neighbour: grab that one instead.
    const crowded = points.findIndex((point) => Math.abs(point.x - x) < MIN_GAP);
    if (crowded >= 0) {
      dragRef.current = { index: crowded, lockedX: points[crowded].x };
      setSelected(crowded);
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }

    const next = [...points, { x, y }].sort((a, b) => a.x - b.x);
    const added = next.findIndex((point) => point.x === x && point.y === y);
    pendingRef.current = next;
    setCurve(channel, next);
    dragRef.current = { index: added, lockedX: x };
    setSelected(added);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const local = localPosition(event);
    if (!local) return;
    setCursor(clamp(local.x, 0, 1));
    const drag = dragRef.current;
    if (!drag) {
      setHover(hitTest(local.px, local.py, local.rect));
      return;
    }
    movePoint(drag.index, event.shiftKey ? drag.lockedX : local.x, local.y, false);
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    commitGesture();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const index = selected ?? (points.length > 0 ? 0 : null);
    if (index === null) return;

    if (event.key === '[' || event.key === 'PageDown') {
      event.preventDefault();
      setSelected(Math.max(0, index - 1));
      return;
    }
    if (event.key === ']' || event.key === 'PageUp') {
      event.preventDefault();
      setSelected(Math.min(points.length - 1, index + 1));
      return;
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      removePoint(index);
      return;
    }

    const step = (event.shiftKey ? 8 : 1) / 255;
    const point = points[index];
    if (!point) return;
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelected(index);
      movePoint(index, point.x, point.y + step, false);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelected(index);
      movePoint(index, point.x, point.y - step, false);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setSelected(index);
      movePoint(index, point.x - step, point.y, false);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setSelected(index);
      movePoint(index, point.x + step, point.y, false);
    }
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key.startsWith('Arrow') && pendingRef.current) commitGesture();
  };

  const readoutIndex = dragRef.current?.index ?? selected ?? hover;
  const readoutPoint = readoutIndex !== null ? points[readoutIndex] : undefined;
  const readInput = readoutPoint ? readoutPoint.x : cursor;
  const readOutput = readoutPoint
    ? readoutPoint.y
    : cursor === null
      ? null
      : lut[Math.round(cursor * (lut.length - 1))];

  const words = describeCurve(points);
  const description =
    words === 'neutral'
      ? 'Neutral, nothing changed on this channel.'
      : `${words.charAt(0).toUpperCase()}${words.slice(1)}.`;

  return (
    <>
      <div className="curve-channels" role="group" aria-label="Curve channel">
        {CHANNELS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={`curve-channel ${entry.id}${entry.id === channel ? ' on' : ''}`}
            aria-pressed={entry.id === channel}
            onClick={() => {
              setChannel(entry.id);
              setSelected(null);
              setHover(null);
              pendingRef.current = null;
            }}
          >
            {entry.label}
            {isNeutralCurve(photo.recipe.curves[entry.id]) ? '' : ' •'}
          </button>
        ))}
      </div>

      <Tip title="Tone curve" body={CURVE_TIP} as="div">
        <div
          ref={containerRef}
          className="curve"
          role="application"
          aria-roledescription="Tone curve editor"
          aria-label={`${channel === 'rgb' ? 'RGB' : channel} tone curve, ${
            points.length
          } points. Click adds, drag moves, alt-click removes. Arrows nudge, brackets step.`}
          aria-describedby={hintId}
          tabIndex={0}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerLeave={() => {
            if (!dragRef.current) {
              setHover(null);
              setCursor(null);
            }
          }}
          onDoubleClick={(event) => {
            const local = localPosition(event);
            if (!local) return;
            const index = hitTest(local.px, local.py, local.rect);
            if (index !== null) removePoint(index);
          }}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
        >
          <canvas ref={setCanvas} />
        </div>
      </Tip>

      <div className="scope-readout" aria-live="polite">
        <span>
          <b>In</b> {readInput === null ? '·' : `${toByte(readInput)} · ${toPercent(readInput)}`}
        </span>
        <span>
          <b>Out</b> {readOutput === null ? '·' : `${toByte(readOutput)} · ${toPercent(readOutput)}`}
        </span>
        <span>
          <b>Pts</b> {points.length}
        </span>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingTop: 8 }}>
        {PRESETS.map((preset) => (
          <button
            key={preset.name}
            type="button"
            className={`btn tiny${samePoints(points, preset.points) ? ' active' : ''}`}
            onClick={() => {
              pendingRef.current = null;
              setSelected(null);
              setCurve(channel, preset.points.map((point) => ({ ...point })), true);
            }}
          >
            {preset.name}
          </button>
        ))}
      </div>

      <div className="curve-hint" id={hintId}>
        {description}
      </div>
    </>
  );
}

export function CurvePanel() {
  const photo = useSelectedPhoto();
  const active =
    photo !== null && CHANNELS.some((entry) => !isNeutralCurve(photo.recipe.curves[entry.id]));

  return (
    <Panel id="curve" title="Tone curve" active={active}>
      <CurveEditor />
    </Panel>
  );
}
